import { createScopedErrors, registerCodes } from '@norkron/web-errors';
import { createContext, forwardRef, useEffect, isValidElement, cloneElement, useRef, useMemo, useState, useContext, useId, Children } from 'react';
import { ErrorBoundary, DefaultErrorFallback } from '@norkron/web-error-boundary';
import '@norkron/web-tooltip';
import { EmptyState } from '@norkron/web-states';
import { useFocusTrap, useDisclosure } from '@norkron/web-a11y';
import { useDismiss } from '@norkron/web-dismiss';
import { useScrollLock, Portal } from '@norkron/web-portal';
import { withConformance, registerResponsiveContract } from '@norkron/web-conformance';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { z } from 'zod';
import { useControlledState } from '@norkron/web-controlled-state';
import { computePosition } from '@norkron/web-floating';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

// src/error-codes.ts
var WEB_POPOVER_LIB = "@norkron/web-popover";
var WEB_POPOVER_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.web-popover");
var { warn, panic, reportInvalidInput } = createScopedErrors(WEB_POPOVER_LIB, WEB_POPOVER_TOKEN);
try {
  registerCodes(WEB_POPOVER_LIB, {
    WEB_POPOVER_INVALID_INPUT: {
      description: "Runtime input validation rejected the value passed to a public web-popover export. Payload follows WEB_INVALID_INPUT_SHAPE: { fieldPath, expected, received }.",
      severity: "warning",
      docsAnchor: "web-popover/invalid-input",
      exampleFix: "Inspect payload.fieldPath + payload.expected; pass input matching the schema exported by this lib."
    },
    WEB_POPOVER_TRIGGER_DETACHED: {
      description: "PopoverContent rendered without a sibling PopoverTrigger inside the same Popover root; positioning falls back to viewport center.",
      severity: "warning",
      docsAnchor: "web-popover/trigger-detached",
      exampleFix: "Add a <PopoverTrigger /> sibling inside the same <Popover> as the <PopoverContent />."
    },
    WEB_POPOVER_NO_PARENT: {
      description: "PopoverContent (or PopoverTrigger) rendered with no <Popover> ancestor \u2014 usePopoverContext could not resolve its parent context; positioning falls back to viewport center.",
      severity: "warning",
      docsAnchor: "web-popover/no-parent",
      exampleFix: "Wrap <PopoverContent /> inside a <Popover> root that also contains a <PopoverTrigger />."
    },
    PO_SELFCHECK_BUDGET_EXCEEDED: {
      description: "selfCheck exceeded its per-call time budget while walking popover instances; partial results returned.",
      severity: "info",
      docsAnchor: "web-popover/self-check#budget-exceeded"
    },
    PO_NO_DISMISS_ON_OUTSIDE: {
      description: "selfCheck found a rendered PopoverContent whose dismiss-on-outside-click affordance is disabled or unreachable. Users may be unable to dismiss the popover by clicking outside.",
      severity: "warning",
      docsAnchor: "web-popover/self-check#no-dismiss-on-outside"
    },
    PO_SELFCHECK_INTERNAL_ERROR: {
      description: "selfCheck threw internally while inspecting a popover instance; reported as a finding with severity info.",
      severity: "info",
      docsAnchor: "web-popover/self-check#internal-error"
    }
  });
} catch {
}
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var LIB_ID = "@norkron/web-popover";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function selfCheck(root, opts) {
  const start = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    const instances = Array.from(
      root.querySelectorAll('[data-norkron-lib^="@norkron/web-popover"]')
    );
    if (instances.length === 0) return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
    for (const instance of instances) {
      const now = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
      if (now - start > safeOpts.budgetMs) {
        findings.push({
          libId: LIB_ID,
          code: "PO_SELFCHECK_BUDGET_EXCEEDED",
          category: "perf",
          severity: "info",
          message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms; partial results returned.`,
          extras: { budgetMs: safeOpts.budgetMs, elapsed: now - start }
        });
        break;
      }
      if (instance.getAttribute("data-state") === "open" && instance.getAttribute("data-dismiss-on-outside-click") !== "true" && instance.getAttribute("data-dismiss-on-outside-click") !== "false") {
        findings.push({
          libId: LIB_ID,
          code: "PO_NO_DISMISS_ON_OUTSIDE",
          category: "flow",
          severity: "minor",
          message: "Popover is open but does not advertise an outside-click dismiss preference. Default Radix-style behaviour is dismiss; the lib should stamp the resolved choice explicitly.",
          selector: selectorFor(instance),
          suggestedFix: "Wire useDismissOnOutsideClick or opt out explicitly via dismissOnOutsideClick={false} plus a sibling close affordance."
        });
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "PO_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}

// src/version.ts
var LIB_VERSION = "0.3.5";

// src/zod-bridge.ts
function toZodLikeSafeParseError(failure) {
  return failure;
}
var PlacementSchema = z.enum([
  "top",
  "top-start",
  "top-end",
  "bottom",
  "bottom-start",
  "bottom-end",
  "left",
  "right"
]);
var PopoverPropsSchema = z.object({
  defaultOpen: z.boolean().optional(),
  open: z.boolean().optional(),
  onOpenChange: z.function().optional(),
  placement: PlacementSchema.optional(),
  modal: z.boolean().optional(),
  id: z.string().optional(),
  children: z.unknown().optional()
});
var PopoverTriggerPropsSchema = z.object({
  children: z.unknown().optional(),
  asChild: z.boolean().optional()
});
var PopoverContentPropsSchema = z.object({
  children: z.unknown().optional(),
  offset: z.number().nonnegative().optional(),
  className: z.string().optional(),
  role: z.enum(["dialog", "tooltip", "menu", "listbox"]).optional(),
  dismissOnOutsideClick: z.boolean().optional(),
  portal: z.boolean().optional(),
  portalContainer: z.unknown().optional(),
  avoidCollisions: z.boolean().optional(),
  collisionPadding: z.number().nonnegative().optional(),
  arrowSize: z.number().nonnegative().optional(),
  onOpenAutoFocus: z.function().optional(),
  onCloseAutoFocus: z.function().optional()
});
var PopoverAnchorPropsSchema = z.object({
  children: z.unknown().optional(),
  asChild: z.boolean().optional()
});
var PopoverClosePropsSchema = z.object({
  children: z.unknown().optional(),
  asChild: z.boolean().optional()
});
var UsePopoverOptionsSchema = z.object({
  defaultOpen: z.boolean().optional(),
  onOpenChange: z.function().optional(),
  id: z.string().optional()
});
var UsePopoverStateSchema = z.object({
  open: z.boolean(),
  show: z.function(),
  hide: z.function(),
  toggle: z.function(),
  triggerId: z.string(),
  contentId: z.string()
});

// src/validate-input.ts
function validateInput(schema, fn, input) {
  const parsed = schema.safeParse(input);
  if (!parsed.success) {
    reportInvalidInput({
      lib: WEB_POPOVER_LIB,
      libToken: WEB_POPOVER_TOKEN,
      fn,
      parseResult: toZodLikeSafeParseError(parsed)
    });
    return input;
  }
  return parsed.data;
}
function usePopover(options = {}) {
  const validated = validateInput(UsePopoverOptionsSchema, "usePopover", options);
  const { defaultOpen = false, onOpenChange, id } = validated;
  const autoId = useId();
  const baseId = id ?? autoId;
  const { open, show, hide, toggle } = useDisclosure({ defaultOpen, onChange: onOpenChange });
  return useMemo(
    () => ({
      open,
      show,
      hide,
      toggle,
      triggerId: `${baseId}-trigger`,
      contentId: `${baseId}-content`
    }),
    [open, show, hide, toggle, baseId]
  );
}
function useControlledPopover(controlled, defaultOpen, onChange) {
  const { value: open, setValue: setOpen } = useControlledState(controlled, defaultOpen, onChange);
  return { open, setOpen };
}
function decomposePlacement(p) {
  if (p === "left") return { side: "left", align: "center" };
  if (p === "right") return { side: "right", align: "center" };
  const dash = p.indexOf("-");
  if (dash < 0) return { side: p, align: "center" };
  const side = p.slice(0, dash);
  const tail = p.slice(dash + 1);
  return { side, align: tail };
}
function virtualEl(rect) {
  const domRect = {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    right: rect.left + rect.width,
    bottom: rect.top + rect.height,
    x: rect.left,
    y: rect.top,
    toJSON: () => ({})
  };
  return { getBoundingClientRect: () => domRect };
}
function computePopoverPosition(input) {
  const { trigger, content, viewport, placement, sideOffset, collisionPadding, avoidCollisions, arrowSize } = input;
  const reference = virtualEl(trigger);
  const floating = virtualEl({ top: 0, left: 0, width: content.width, height: content.height });
  const boundary = virtualEl({ top: 0, left: 0, width: viewport.width, height: viewport.height });
  const result = computePosition(reference, floating, {
    placement,
    offset: sideOffset,
    collisionPadding: avoidCollisions ? collisionPadding : 0,
    collisionBoundary: boundary
  });
  if (!avoidCollisions) {
    const { side, align } = decomposePlacement(placement);
    const raw = placeRaw(side, align, trigger, content, sideOffset);
    const out2 = { top: raw.top, left: raw.left, side, align, flipped: false };
    if (typeof arrowSize === "number" && arrowSize > 0) {
      out2.arrow = computeArrowPlacement(side, trigger, content, { top: raw.top, left: raw.left }, arrowSize);
    }
    return out2;
  }
  const out = {
    top: result.y,
    left: result.x,
    side: result.side,
    align: result.align,
    flipped: result.flipped
  };
  if (typeof arrowSize === "number" && arrowSize > 0) {
    out.arrow = computeArrowPlacement(result.side, trigger, content, { top: result.y, left: result.x }, arrowSize);
  }
  return out;
}
function computeArrowPlacement(side, trigger, content, clamped, arrowSize) {
  if (side === "top" || side === "bottom") {
    const triggerCenterX = trigger.left + trigger.width / 2;
    const desiredLeft = triggerCenterX - clamped.left;
    const min2 = arrowSize;
    const max2 = content.width - arrowSize;
    const left2 = Math.max(min2, Math.min(desiredLeft, max2));
    const top2 = side === "top" ? content.height : 0;
    return { top: top2, left: left2 };
  }
  const triggerCenterY = trigger.top + trigger.height / 2;
  const desiredTop = triggerCenterY - clamped.top;
  const min = arrowSize;
  const max = content.height - arrowSize;
  const top = Math.max(min, Math.min(desiredTop, max));
  const left = side === "left" ? content.width : 0;
  return { top, left };
}
function placeRaw(side, align, trigger, content, sideOffset) {
  let top = 0;
  let left = 0;
  if (side === "top" || side === "bottom") {
    top = side === "top" ? trigger.top - content.height - sideOffset : trigger.top + trigger.height + sideOffset;
    const start = trigger.left;
    const center = trigger.left + trigger.width / 2 - content.width / 2;
    const end = trigger.left + trigger.width - content.width;
    left = align === "start" ? start : align === "end" ? end : center;
  } else {
    left = side === "left" ? trigger.left - content.width - sideOffset : trigger.left + trigger.width + sideOffset;
    const start = trigger.top;
    const center = trigger.top + trigger.height / 2 - content.height / 2;
    const end = trigger.top + trigger.height - content.height;
    top = align === "start" ? start : align === "end" ? end : center;
  }
  return { top, left };
}
function recomposePlacement(side, align) {
  if (side === "left" || side === "right") return side;
  if (align === "center") return side;
  return `${side}-${align}`;
}
var WEB_POPOVER_LIB2 = "@norkron/web-popover";
var PopoverArrowContext = createContext(null);
function PopoverArrow({ size, className, style }) {
  const ctx = useContext(PopoverArrowContext);
  if (!ctx) return null;
  const { side, arrow, arrowSize: ctxSize } = ctx;
  const resolvedSize = size ?? ctxSize;
  const measured = arrow !== null;
  const finalStyle = {
    position: "absolute",
    // Drive sizing via custom property so consumer CSS overrides land.
    ["--popover-arrow-size"]: `${resolvedSize}px`,
    width: `${resolvedSize * 2}px`,
    height: `${resolvedSize * 2}px`,
    // Offset by -arrowSize so `{top,left}` describes the arrow CENTRE.
    top: measured ? `${(arrow?.top ?? 0) - resolvedSize}px` : "0px",
    left: measured ? `${(arrow?.left ?? 0) - resolvedSize}px` : "0px",
    visibility: measured ? "visible" : "hidden",
    pointerEvents: "none",
    ...style
  };
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: className ?? "popover__arrow",
      "data-side": side,
      "data-testid": "popover__arrow",
      "aria-hidden": "true",
      style: finalStyle,
      ...withConformance(WEB_POPOVER_LIB2, LIB_VERSION)
    }
  );
}
registerLazy(
  WEB_POPOVER_LIB,
  () => selfCheck
);
var PopoverContext = createContext(null);
function usePopoverContext(fn) {
  const ctx = useContext(PopoverContext);
  if (!ctx) {
    warn({
      fn,
      code: "WEB_POPOVER_NO_PARENT",
      message: `${fn} rendered outside <Popover>`
    });
  }
  return ctx;
}
function Popover(props) {
  const validated = validateInput(PopoverPropsSchema, "Popover", props);
  const { defaultOpen = false, open: controlled, onOpenChange, placement = "bottom", modal = false, id, children } = validated;
  const state = usePopover({ defaultOpen, id, onOpenChange });
  const { open, setOpen } = useControlledPopover(controlled, defaultOpen, onOpenChange);
  const triggerRef = useRef(null);
  const anchorRef = useRef(null);
  const triggerCountRef = useRef(0);
  const registerTrigger = () => {
    triggerCountRef.current += 1;
    return () => {
      triggerCountRef.current -= 1;
    };
  };
  const warnedRef = useRef(false);
  const hasTrigger = containsPopoverTrigger(children);
  if (!warnedRef.current && !hasTrigger) {
    warnedRef.current = true;
    warn({
      fn: "Popover",
      code: "WEB_POPOVER_TRIGGER_DETACHED",
      message: "<Popover> rendered without a <PopoverTrigger> descendant \u2014 positioning falls back to viewport center."
    });
  }
  const effective = {
    ...state,
    open: controlled !== void 0 ? open : state.open,
    show: () => {
      if (controlled === void 0) state.show();
      setOpen(true);
    },
    hide: () => {
      if (controlled === void 0) state.hide();
      setOpen(false);
    },
    toggle: () => {
      if (controlled === void 0) state.toggle();
      setOpen(!effective.open);
    }
  };
  return /* @__PURE__ */ jsx(PopoverContext.Provider, { value: { ...effective, placement, modal, triggerRef, anchorRef, registerTrigger, hasTrigger }, children: /* @__PURE__ */ jsx(
    ErrorBoundary,
    {
      fallback: (err, reset) => /* @__PURE__ */ jsx(DefaultErrorFallback, { error: err, reset }),
      children
    }
  ) });
}
var PopoverTrigger = forwardRef(
  function PopoverTrigger2(props, ref) {
    const validated = validateInput(PopoverTriggerPropsSchema, "PopoverTrigger", props);
    const ctx = usePopoverContext("PopoverTrigger");
    const { children, asChild } = validated;
    useEffect(() => {
      if (!ctx) return;
      const unregister = ctx.registerTrigger();
      return unregister;
    }, [ctx]);
    const onClick = () => ctx?.toggle();
    const visibleStringChildText = typeof children === "string" ? children.trim() : "";
    const needsDefaultAriaLabel = visibleStringChildText.length > 0 && visibleStringChildText.length <= 3;
    const ariaProps = {
      "aria-expanded": ctx?.open ?? false,
      "aria-controls": ctx?.open ? ctx?.contentId : void 0,
      "aria-haspopup": "dialog",
      ...needsDefaultAriaLabel ? { "aria-label": "Open popover" } : {},
      id: ctx?.triggerId
    };
    if (asChild && isValidElement(children)) {
      const setRef = (node) => {
        if (ctx) ctx.triggerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      };
      const childProps = children.props;
      const existingOnClick = childProps.onClick;
      return cloneElement(children, {
        ...ariaProps,
        ref: setRef,
        onClick: (e) => {
          existingOnClick?.(e);
          onClick();
        }
      });
    }
    return /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        ref: (node) => {
          if (ctx) ctx.triggerRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        },
        className: "popover__trigger",
        "data-allow-unwired": "true",
        "data-allow-edge-text": "true",
        ...withConformance(WEB_POPOVER_LIB, LIB_VERSION),
        onClick,
        ...ariaProps,
        children
      }
    );
  }
);
var PopoverContent = forwardRef(
  function PopoverContent2(props, ref) {
    const validated = validateInput(PopoverContentPropsSchema, "PopoverContent", props);
    const ctx = usePopoverContext("PopoverContent");
    const {
      children,
      offset = 8,
      className,
      role = "dialog",
      dismissOnOutsideClick = true,
      portal = true,
      portalContainer,
      avoidCollisions = true,
      collisionPadding = 8,
      arrowSize = 6,
      onOpenAutoFocus,
      onCloseAutoFocus
    } = validated;
    const localRef = useRef(null);
    const setRef = (node2) => {
      localRef.current = node2;
      if (typeof ref === "function") ref(node2);
      else if (ref) ref.current = node2;
    };
    useFocusTrap(
      localRef,
      Boolean(ctx?.open && ctx?.modal)
    );
    useScrollLock(Boolean(ctx?.open && ctx?.modal));
    const wasOpenRef = useRef(false);
    const triggerForRestoreRef = useRef(null);
    useEffect(() => {
      const isOpen = Boolean(ctx?.open);
      const wasOpen = wasOpenRef.current;
      if (isOpen && !wasOpen) {
        triggerForRestoreRef.current = ctx?.triggerRef.current ?? null;
        const evt = new Event("popover.openAutoFocus", { cancelable: true, bubbles: false });
        try {
          onOpenAutoFocus?.(evt);
        } catch {
        }
        if (!evt.defaultPrevented) {
          if (!ctx?.modal) {
            const node2 = localRef.current;
            if (node2) {
              const focusable = node2.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
              );
              (focusable ?? node2).focus?.();
            }
          }
        }
      } else if (!isOpen && wasOpen) {
        const evt = new Event("popover.closeAutoFocus", { cancelable: true, bubbles: false });
        try {
          onCloseAutoFocus?.(evt);
        } catch {
        }
        if (!evt.defaultPrevented) {
          triggerForRestoreRef.current?.focus?.();
        }
        triggerForRestoreRef.current = null;
      }
      wasOpenRef.current = isOpen;
    }, [ctx?.open, ctx?.modal, ctx?.triggerRef, onOpenAutoFocus, onCloseAutoFocus]);
    const dismissRefs = useMemo(
      () => [localRef, ctx?.triggerRef ?? { current: null }],
      [ctx?.triggerRef]
    );
    useDismiss({
      enabled: Boolean(ctx?.open),
      refs: dismissRefs,
      onDismiss: (reason) => {
        if (reason === "outside-pointer" && !dismissOnOutsideClick) return;
        ctx?.hide();
      }
    });
    const [, setMeasureTick] = useState(0);
    useEffect(() => {
      if (!ctx?.open) return;
      setMeasureTick((t) => t + 1);
    }, [ctx?.open]);
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
      setMounted(true);
    }, []);
    if (!ctx?.open) return null;
    const placement = ctx.placement;
    const positionRef = ctx.anchorRef?.current ?? ctx.triggerRef.current;
    const baseStyle = computeCollisionAwarePlacementStyle(
      positionRef,
      localRef.current,
      placement,
      offset,
      avoidCollisions,
      collisionPadding,
      arrowSize
    );
    const resolvedSide = baseStyle.__resolvedSide;
    const resolvedAlign = baseStyle.__resolvedAlign;
    const flipped = baseStyle.__flipped;
    const arrowPlacement = baseStyle.__arrow;
    const resolvedPlacement = recomposePlacement(resolvedSide, resolvedAlign);
    const style = baseStyle.style;
    const isCenterFallback = baseStyle.__fallback === "center";
    const suppressPlacementClass = isCenterFallback && ctx?.hasTrigger === false;
    const cls = [
      "popover__content",
      suppressPlacementClass ? null : `popover__content--${resolvedPlacement}`,
      className
    ].filter(Boolean).join(" ");
    const body = children == null ? /* @__PURE__ */ jsx(EmptyState, { title: "Nothing to show", description: "Popover content is empty." }) : children;
    const surface = /* @__PURE__ */ jsx(PopoverArrowContext.Provider, { value: { side: resolvedSide, arrow: arrowPlacement, arrowSize }, children: /* @__PURE__ */ jsx(
      "div",
      {
        ref: setRef,
        role,
        id: ctx.contentId,
        "aria-labelledby": ctx.hasTrigger ? ctx.triggerId : void 0,
        className: cls,
        style,
        "data-state": ctx.open ? "open" : "closed",
        "data-placement": resolvedPlacement,
        "data-side": resolvedSide,
        "data-align": resolvedAlign,
        "data-flipped": flipped ? "true" : "false",
        "data-portal": portal ? "true" : "false",
        "data-dismiss-on-outside-click": dismissOnOutsideClick ? "true" : "false",
        "data-keep-clear": "true",
        "data-popover-fallback": baseStyle.__fallback ?? void 0,
        ...withConformance(WEB_POPOVER_LIB, LIB_VERSION),
        children: body
      }
    ) });
    const node = ctx.modal ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          className: "popover__overlay",
          "data-popover-overlay": "true",
          "data-state": "open",
          "aria-hidden": "true",
          ...withConformance(WEB_POPOVER_LIB, LIB_VERSION)
        }
      ),
      surface
    ] }) : surface;
    if (!portal || !mounted) return node;
    return /* @__PURE__ */ jsx(Portal, { container: portalContainer ?? void 0, children: node });
  }
);
var PopoverAnchor = forwardRef(
  function PopoverAnchor2(props, ref) {
    const validated = validateInput(PopoverAnchorPropsSchema, "PopoverAnchor", props);
    const ctx = usePopoverContext("PopoverAnchor");
    const { children, asChild } = validated;
    const setAnchor = (node) => {
      if (ctx) ctx.anchorRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };
    useEffect(() => {
      return () => {
        if (ctx && ctx.anchorRef.current) {
          ctx.anchorRef.current = null;
        }
      };
    }, [ctx]);
    if (asChild && isValidElement(children)) {
      const childProps = children.props;
      const existingRef = children.ref;
      const mergedRef = (node) => {
        setAnchor(node);
        if (typeof existingRef === "function") existingRef(node);
        else if (existingRef && typeof existingRef === "object") existingRef.current = node;
      };
      return cloneElement(children, {
        ...childProps,
        ref: mergedRef
      });
    }
    return /* @__PURE__ */ jsx(
      "span",
      {
        ref: setAnchor,
        className: "popover__anchor",
        "data-popover-anchor": "true",
        children
      }
    );
  }
);
var PopoverClose = forwardRef(
  function PopoverClose2(props, ref) {
    const validated = validateInput(PopoverClosePropsSchema, "PopoverClose", props);
    const ctx = usePopoverContext("PopoverClose");
    const { children, asChild, onClick: consumerOnClick } = validated;
    const handleClick = (e) => {
      consumerOnClick?.(e);
      if (e.defaultPrevented) return;
      ctx?.hide();
    };
    if (asChild && isValidElement(children)) {
      const childProps = children.props;
      const existingOnClick = childProps.onClick;
      return cloneElement(children, {
        ...childProps,
        ref,
        onClick: (e) => {
          existingOnClick?.(e);
          handleClick(e);
        }
      });
    }
    return /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        ref,
        className: "popover__close",
        "data-popover-close": "true",
        "data-allow-unwired": "true",
        "data-allow-edge-text": "true",
        onClick: handleClick,
        children
      }
    );
  }
);
function containsPopoverTrigger(node) {
  let found = false;
  const visit = (n) => {
    if (found) return;
    if (n == null || typeof n === "boolean") return;
    if (Array.isArray(n)) {
      for (const child of n) {
        if (found) return;
        visit(child);
      }
      return;
    }
    if (!isValidElement(n)) return;
    if (n.type === PopoverTrigger) {
      found = true;
      return;
    }
    const childProps = n.props;
    if (childProps && "children" in childProps) {
      visit(childProps.children);
    }
  };
  Children.forEach(node, visit);
  return found;
}
function computeCollisionAwarePlacementStyle(trigger, contentEl, placement, offset, avoidCollisions, collisionPadding, arrowSize) {
  if (!trigger) {
    const fallback = ssrFallback(placement);
    const noWindow = typeof window === "undefined";
    if (noWindow) return fallback;
    return {
      style: { position: "fixed", top: "50%", left: "50%" },
      __resolvedSide: fallback.__resolvedSide,
      __resolvedAlign: fallback.__resolvedAlign,
      __flipped: false,
      __arrow: null,
      __fallback: "center"
    };
  }
  if (typeof window === "undefined") {
    return ssrFallback(placement);
  }
  const triggerRect = trigger.getBoundingClientRect();
  const contentRect = contentEl ? contentEl.getBoundingClientRect() : { width: 0, height: 0 };
  const result = computePopoverPosition({
    trigger: { top: triggerRect.top, left: triggerRect.left, width: triggerRect.width, height: triggerRect.height },
    content: { top: 0, left: 0, width: contentRect.width, height: contentRect.height },
    viewport: { width: window.innerWidth, height: window.innerHeight },
    placement,
    sideOffset: offset,
    collisionPadding,
    avoidCollisions,
    arrowSize: arrowSize > 0 && contentEl ? arrowSize : void 0
  });
  return {
    style: { position: "fixed", top: Math.round(result.top), left: Math.round(result.left) },
    __resolvedSide: result.side,
    __resolvedAlign: result.align,
    __flipped: result.flipped,
    __arrow: result.arrow ?? null
  };
}
function decomposePlacementLocal(p) {
  if (p === "left") return { side: "left", align: "center" };
  if (p === "right") return { side: "right", align: "center" };
  const dash = p.indexOf("-");
  if (dash < 0) return { side: p, align: "center" };
  return { side: p.slice(0, dash), align: p.slice(dash + 1) };
}
function ssrFallback(placement) {
  const { side, align } = decomposePlacementLocal(placement);
  return {
    style: { position: "fixed", top: 0, left: 0 },
    __resolvedSide: side,
    __resolvedAlign: align,
    __flipped: false,
    __arrow: null,
    __fallback: "ssr"
  };
}
var selfCheckInputSchema = z.object({
  opts: SelfCheckOptionsSchema.optional()
}).strict();
var ConformanceSeveritySchema = z.enum(["critical", "major", "minor", "info"]);
var ConformanceCategorySchema = z.enum([
  "theme",
  "stylesheet",
  "a11y",
  "layout",
  "i18n",
  "cardinality",
  "identity",
  "schema",
  "flow",
  "deprecation",
  "perf"
]);
var SelfCheckFindingSchema = z.object({
  libId: z.string().min(1),
  code: z.string().min(1),
  category: ConformanceCategorySchema,
  severity: ConformanceSeveritySchema,
  message: z.string().min(1),
  selector: z.string().optional(),
  suggestedFix: z.string().optional(),
  extras: z.record(z.unknown()).optional()
}).strict();
var LIB_VERSIONSchema = z.string().regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z-.]+)?$/, "LIB_VERSION must be semver");
var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-popover",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    // Grounded in popover.css:22-24 — JS-positioned fixed surface, single
    // min-width floor, NO @media. Honest baseline: 'full' at every tier.
    xs: "full",
    sm: "full",
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [],
  rwdSelfCheckCodes: []
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);

export { ConformanceCategorySchema, ConformanceSeveritySchema, LIB_VERSION, LIB_VERSIONSchema, PlacementSchema, Popover, PopoverAnchor, PopoverAnchorPropsSchema, PopoverArrow, PopoverClose, PopoverClosePropsSchema, PopoverContent, PopoverContentPropsSchema, PopoverPropsSchema, PopoverTrigger, PopoverTriggerPropsSchema, RESPONSIVE_CONTRACT, SelfCheckFindingSchema, SelfCheckOptionsSchema, UsePopoverOptionsSchema, UsePopoverStateSchema, computePopoverPosition, decomposePlacement, recomposePlacement, selfCheck, selfCheckInputSchema, usePopover };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map